import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, sendPasswordResetEmail, deleteUser } from 'firebase/auth';
import { auth } from '../../firebase'; // adjust path
import styles from './AccountSettings.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AccountSettings() {
  const { currentUser, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch real profile data (encrypted fields decrypted by backend)
  useEffect(() => {
    if (!currentUser) return;

    setDisplayName(currentUser.displayName || '');
    setEmail(currentUser.email || '');

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/user/profile`, {
          headers: {
            'x-user-id': currentUser.uid
          }
        });

        if (!res.ok) throw new Error('Failed to load profile');
        const profile = await res.json();

        setPhone(profile.phone || '');
        setLocation(profile.location || '');
        setBio(profile.bio || '');
      } catch (err) {
        setError('Could not load profile data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Update Firebase display name (optional)
      if (displayName.trim() !== currentUser.displayName) {
        await updateProfile(auth.currentUser, {
          displayName: displayName.trim()
        });
      }

      // Save encrypted fields to backend
      const res = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': currentUser.uid
        },
        body: JSON.stringify({
          phone: phone.trim() || null,
          location: location.trim() || null,
          bio: bio.trim() || null
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to save profile');
      }

      setSuccess('Profile updated successfully!');
      setEditMode(false);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;

    setError('');
    try {
      await deleteUser(auth.currentUser);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to delete account');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.loading}>Loading account...</div>
        </div>
      </main>
    );
  }

  if (!currentUser) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.error}>
            Please sign in to access account settings.
          </div>
          <Link to="/login" className={styles.btnPrimary}>
            Go to Sign In
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* Back / Title */}
        <header className={styles.pageHeader}>
          <Link to="/" className={styles.backLink}>
            <Icon icon="lucide:arrow-left" />
            Back to Home
          </Link>
          <h1 className={styles.pageTitle}>Account Settings</h1>
        </header>

        <div className={styles.layout}>
          {/* Sidebar Navigation */}
          <aside className={styles.sidebar}>
            <nav className={styles.navList}>
              <Link to="/account-settings" className={`${styles.navItem} ${styles.active}`}>
                <Icon icon="lucide:user" />
                Personal Information
              </Link>
              <Link to="/account-settings/organization" className={styles.navItem}>
                <Icon icon="lucide:building" />
                Organization & Role
              </Link>
              <Link to="/account-settings/privacy" className={styles.navItem}>
                <Icon icon="lucide:shield" />
                Privacy Controls
              </Link>
              <Link to="/account-settings/security" className={styles.navItem}>
                <Icon icon="lucide:lock" />
                Password & Authentication
              </Link>
              <Link to="/account-settings/notifications" className={styles.navItem}>
                <Icon icon="lucide:bell" />
                Notification Preferences
              </Link>
              <Link to="/account-settings/emergency" className={styles.navItem}>
                <Icon icon="lucide:alert-triangle" />
                Emergency Alerts & Contacts
              </Link>
              <Link to="/account-settings/data" className={styles.navItem}>
                <Icon icon="lucide:download" />
                Data Export & Activity
              </Link>
              <Link to="/support" className={styles.navItem}>
                <Icon icon="lucide:help-circle" />
                Help & Support
              </Link>
              <button onClick={handleLogout} className={`${styles.navItem} ${styles.danger}`}>
                <Icon icon="lucide:log-out" />
                Sign Out
              </button>
              <button onClick={handleDeleteAccount} className={`${styles.navItem} ${styles.danger}`}>
                <Icon icon="lucide:trash-2" />
                Delete Account
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <div className={styles.content}>
            {/* Profile Section */}
            <section className={styles.section}>
              <div className={styles.profileHeader}>
                <div className={styles.avatarWrapper}>
                  <img
                    src={currentUser.photoURL || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300"}
                    alt={currentUser.displayName || 'User'}
                    className={styles.avatar}
                  />
                  <button className={styles.editAvatar}>
                    <Icon icon="lucide:camera" />
                  </button>
                </div>

                <div className={styles.profileInfo}>
                  <div className={styles.nameRow}>
                    <h2 className={styles.name}>{currentUser.displayName || 'User'}</h2>
                    <span className={styles.verifiedBadge}>
                      <Icon icon="lucide:shield-check" />
                      Verified
                    </span>
                  </div>
                  <p className={styles.role}>Human Rights Advocate</p>

                  <div className={styles.profileActions}>
                    <button className={styles.btnOutline} onClick={() => setEditMode(!editMode)}>
                      {editMode ? 'Cancel' : 'Edit Profile'}
                    </button>
                    <Link to="/profile/public" className={styles.btnOutline}>
                      View Public Profile
                    </Link>
                  </div>
                </div>
              </div>

              {editMode ? (
                <form onSubmit={handleSaveProfile} className={styles.formGrid}>
                  <div className={styles.formField}>
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your name"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Email Address</label>
                    <input type="email" value={email} disabled />
                  </div>
                  <div className={styles.formField}>
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Location</label>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </div>
                  <div className={styles.formFieldFull}>
                    <label>Bio</label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      rows={4}
                    />
                  </div>

                  <div className={styles.formActions}>
                    <button type="submit" className={styles.btnPrimary} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                  {success && <p className={styles.success}>{success}</p>}
                  {error && <p className={styles.error}>{error}</p>}
                </form>
              ) : (
                <div className={styles.profileView}>
                  <p><strong>Phone:</strong> {phone || 'Not set'}</p>
                  <p><strong>Location:</strong> {location || 'Not set'}</p>
                  <p><strong>Bio:</strong> {bio || 'Not set'}</p>
                </div>
              )}
            </section>

            {/* Privacy & Sharing */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Privacy & Sharing</h2>

              <div className={styles.settingRow}>
                <div>
                  <div className={styles.settingLabel}>Resource Visibility</div>
                  <div className={styles.settingHelp}>Who can see your saved/bookmarked resources</div>
                </div>
                <select className={styles.select}>
                  <option>Verified Members Only</option>
                  <option>Organization Members</option>
                  <option>Public</option>
                  <option>Private</option>
                </select>
              </div>

              <div className={styles.settingRow}>
                <div>
                  <div className={styles.settingLabel}>Activity Status</div>
                  <div className={styles.settingHelp}>Show when you were last active</div>
                </div>
                <label className={styles.toggleSwitch}>
                  <input type="checkbox" defaultChecked />
                  <span className={styles.slider}></span>
                </label>
              </div>

              <div className={styles.settingRow}>
                <div>
                  <div className={styles.settingLabel}>Data Analytics</div>
                  <div className={styles.settingHelp}>Allow anonymous usage data to improve SafeGuard</div>
                </div>
                <label className={styles.toggleSwitch}>
                  <input type="checkbox" defaultChecked />
                  <span className={styles.slider}></span>
                </label>
              </div>
            </section>

            {/* Security & Authentication */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Security & Authentication</h2>

              <div className={styles.securityRow}>
                <div>
                  <div className={styles.settingLabel}>Two-Factor Authentication</div>
                  <div className={styles.settingHelp}>Enabled via authenticator app (not configured)</div>
                </div>
                <span className={styles.badgeSuccess}>Enabled</span>
              </div>

              <div className={styles.securityRow}>
                <div>
                  <div className={styles.settingLabel}>Password</div>
                  <div className={styles.settingHelp}>Last changed recently</div>
                </div>
                <button className={styles.btnOutlineSmall} onClick={handleChangePassword}>
                  Change Password
                </button>
              </div>

              <div className={styles.securityRow}>
                <div>
                  <div className={styles.settingLabel}>Active Sessions</div>
                  <div className={styles.settingHelp}>Current device</div>
                </div>
                <button className={styles.btnOutlineSmall}>Manage Sessions</button>
              </div>
            </section>

            {/* Emergency Contacts */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Emergency Contacts</h2>

              <div className={styles.emergencyCard}>
                <div className={styles.emergencyIcon}>
                  <Icon icon="lucide:phone-call" />
                </div>
                <div>
                  <h3>Primary Emergency Contact</h3>
                  <p>Not set yet</p>
                </div>
                <button className={styles.editBtn}>Edit</button>
              </div>

              <button className={styles.addBtn}>
                <Icon icon="lucide:plus" />
                Add Emergency Contact
              </button>
            </section>

            {/* Data Export & Delete */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Account Data & Export</h2>

              <div className={styles.dataActions}>
                <div className={styles.dataCard}>
                  <Icon icon="lucide:download" className={styles.dataIcon} />
                  <div>
                    <h3>Export Data</h3>
                    <p>Download all your account data</p>
                  </div>
                  <button className={styles.btnPrimarySmall}>Request Export</button>
                </div>

                <div className={styles.dataCard}>
                  <Icon icon="lucide:activity" className={styles.dataIcon} />
                  <div>
                    <h3>Activity Log</h3>
                    <p>View recent account activity</p>
                  </div>
                  <button className={styles.btnOutlineSmall}>View Log</button>
                </div>
              </div>

              <div className={styles.dangerZone}>
                <h3>Delete Account</h3>
                <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
                <button className={styles.btnDanger} onClick={handleDeleteAccount}>
                  Delete My Account
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
// import { useState, useEffect } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import { useAuth } from '../../context/AuthContext';
// import { updateProfile, sendPasswordResetEmail, deleteUser } from 'firebase/auth';
// import { auth } from '../../firebase'; // adjust path
// import styles from './AccountSettings.module.css';

// export default function AccountSettings() {
//   const { currentUser, logout, loading: authLoading } = useAuth();
//   const navigate = useNavigate();

//   const [displayName, setDisplayName] = useState('');
//   const [email, setEmail] = useState('');
//   const [phone, setPhone] = useState('');
//   const [location, setLocation] = useState('');
//   const [bio, setBio] = useState('');
//   const [editMode, setEditMode] = useState(false);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');
//   const [loading, setLoading] = useState(false);

//   // Load user data on mount
//   useEffect(() => {
//     if (currentUser) {
//       setDisplayName(currentUser.displayName || '');
//       setEmail(currentUser.email || '');
//       // Phone/location/bio - not in Firebase by default; use localStorage or DB later
//       setPhone(localStorage.getItem('phone') || '');
//       setLocation(localStorage.getItem('location') || '');
//       setBio(localStorage.getItem('bio') || '');
//     }
//   }, [currentUser]);

//   if (authLoading) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.loading}>Loading account...</div>
//         </div>
//       </main>
//     );
//   }

//   if (!currentUser) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.error}>
//             Please sign in to access account settings.
//           </div>
//           <Link to="/login" className={styles.btnPrimary}>
//             Go to Sign In
//           </Link>
//         </div>
//       </main>
//     );
//   }

//   const handleSaveProfile = async (e) => {
//     e.preventDefault();
//     setError('');
//     setSuccess('');
//     setLoading(true);

//     try {
//       // Update Firebase profile (displayName, photoURL)
//       await updateProfile(auth.currentUser, {
//         displayName: displayName.trim(),
//         // photoURL: if you add photo upload later
//       });

//       // Save extra fields locally (or to DB later)
//       localStorage.setItem('phone', phone.trim());
//       localStorage.setItem('location', location.trim());
//       localStorage.setItem('bio', bio.trim());

//       setSuccess('Profile updated successfully!');
//       setEditMode(false);
//     } catch (err) {
//       setError(err.message || 'Failed to update profile');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChangePassword = async () => {
//     setError('');
//     setSuccess('');

//     try {
//       await sendPasswordResetEmail(auth, currentUser.email);
//       setSuccess('Password reset email sent! Check your inbox.');
//     } catch (err) {
//       setError(err.message || 'Failed to send reset email');
//     }
//   };

//   const handleDeleteAccount = async () => {
//     if (!window.confirm('Are you sure? This cannot be undone.')) return;

//     setError('');
//     try {
//       await deleteUser(auth.currentUser);
//       navigate('/');
//     } catch (err) {
//       setError(err.message || 'Failed to delete account');
//     }
//   };

//   const handleLogout = async () => {
//     await logout();
//     navigate('/');
//   };

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Back / Title */}
//         <header className={styles.pageHeader}>
//           <Link to="/" className={styles.backLink}>
//             <Icon icon="lucide:arrow-left" />
//             Back to Home
//           </Link>
//           <h1 className={styles.pageTitle}>Account Settings</h1>
//         </header>

//         <div className={styles.layout}>
//           {/* Sidebar Navigation */}
//           <aside className={styles.sidebar}>
//             <nav className={styles.navList}>
//               <Link to="/account-settings" className={`${styles.navItem} ${styles.active}`}>
//                 <Icon icon="lucide:user" />
//                 Personal Information
//               </Link>
//               <Link to="/account-settings/organization" className={styles.navItem}>
//                 <Icon icon="lucide:building" />
//                 Organization & Role
//               </Link>
//               <Link to="/account-settings/privacy" className={styles.navItem}>
//                 <Icon icon="lucide:shield" />
//                 Privacy Controls
//               </Link>
//               <Link to="/account-settings/security" className={styles.navItem}>
//                 <Icon icon="lucide:lock" />
//                 Password & Authentication
//               </Link>
//               <Link to="/account-settings/notifications" className={styles.navItem}>
//                 <Icon icon="lucide:bell" />
//                 Notification Preferences
//               </Link>
//               <Link to="/account-settings/emergency" className={styles.navItem}>
//                 <Icon icon="lucide:alert-triangle" />
//                 Emergency Alerts & Contacts
//               </Link>
//               <Link to="/account-settings/data" className={styles.navItem}>
//                 <Icon icon="lucide:download" />
//                 Data Export & Activity
//               </Link>
//               <Link to="/support" className={styles.navItem}>
//                 <Icon icon="lucide:help-circle" />
//                 Help & Support
//               </Link>
//               <button onClick={handleLogout} className={`${styles.navItem} ${styles.danger}`}>
//                 <Icon icon="lucide:log-out" />
//                 Sign Out
//               </button>
//               <button onClick={handleDeleteAccount} className={`${styles.navItem} ${styles.danger}`}>
//                 <Icon icon="lucide:trash-2" />
//                 Delete Account
//               </button>
//             </nav>
//           </aside>

//           {/* Main Content */}
//           <div className={styles.content}>
//             {/* Profile Section */}
//             <section className={styles.section}>
//               <div className={styles.profileHeader}>
//                 <div className={styles.avatarWrapper}>
//                   <img
//                     src={currentUser.photoURL || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300"}
//                     alt={currentUser.displayName || 'User'}
//                     className={styles.avatar}
//                   />
//                   <button className={styles.editAvatar}>
//                     <Icon icon="lucide:camera" />
//                   </button>
//                 </div>

//                 <div className={styles.profileInfo}>
//                   <div className={styles.nameRow}>
//                     <h2 className={styles.name}>{currentUser.displayName || 'User'}</h2>
//                     {/* Verified badge - Firebase doesn't have this; optional */}
//                     <span className={styles.verifiedBadge}>
//                       <Icon icon="lucide:shield-check" />
//                       Verified
//                     </span>
//                   </div>
//                   <p className={styles.role}>Human Rights Advocate</p> {/* Placeholder - add role later */}

//                   <div className={styles.profileActions}>
//                     <button className={styles.btnOutline} onClick={() => setEditMode(!editMode)}>
//                       {editMode ? 'Cancel' : 'Edit Profile'}
//                     </button>
//                     <Link to="/profile/public" className={styles.btnOutline}>
//                       View Public Profile
//                     </Link>
//                   </div>
//                 </div>
//               </div>

//               {editMode ? (
//                 <form onSubmit={handleSaveProfile} className={styles.formGrid}>
//                   <div className={styles.formField}>
//                     <label>Full Name</label>
//                     <input
//                       type="text"
//                       value={displayName}
//                       onChange={(e) => setDisplayName(e.target.value)}
//                       placeholder="Your name"
//                     />
//                   </div>
//                   <div className={styles.formField}>
//                     <label>Email Address</label>
//                     <input
//                       type="email"
//                       value={email}
//                       disabled // Email can't be changed easily in Firebase
//                     />
//                   </div>
//                   <div className={styles.formField}>
//                     <label>Phone Number</label>
//                     <input
//                       type="tel"
//                       value={phone}
//                       onChange={(e) => setPhone(e.target.value)}
//                     />
//                   </div>
//                   <div className={styles.formField}>
//                     <label>Location</label>
//                     <input
//                       type="text"
//                       value={location}
//                       onChange={(e) => setLocation(e.target.value)}
//                     />
//                   </div>
//                   <div className={styles.formFieldFull}>
//                     <label>Bio</label>
//                     <textarea
//                       value={bio}
//                       onChange={(e) => setBio(e.target.value)}
//                       rows={4}
//                     />
//                   </div>

//                   <div className={styles.formActions}>
//                     <button type="submit" className={styles.btnPrimary} disabled={loading}>
//                       {loading ? 'Saving...' : 'Save Changes'}
//                     </button>
//                   </div>
//                   {success && <p className={styles.success}>{success}</p>}
//                   {error && <p className={styles.error}>{error}</p>}
//                 </form>
//               ) : (
//                 <div className={styles.profileView}>
//                   {/* Display saved fields */}
//                   <p><strong>Phone:</strong> {phone || 'Not set'}</p>
//                   <p><strong>Location:</strong> {location || 'Not set'}</p>
//                   <p><strong>Bio:</strong> {bio || 'Not set'}</p>
//                 </div>
//               )}
//             </section>

//             {/* Privacy & Sharing */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Privacy & Sharing</h2>

//               <div className={styles.settingRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Resource Visibility</div>
//                   <div className={styles.settingHelp}>Who can see your saved/bookmarked resources</div>
//                 </div>
//                 <select className={styles.select}>
//                   <option>Verified Members Only</option>
//                   <option>Organization Members</option>
//                   <option>Public</option>
//                   <option>Private</option>
//                 </select>
//               </div>

//               <div className={styles.settingRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Activity Status</div>
//                   <div className={styles.settingHelp}>Show when you were last active</div>
//                 </div>
//                 <label className={styles.toggleSwitch}>
//                   <input type="checkbox" defaultChecked />
//                   <span className={styles.slider}></span>
//                 </label>
//               </div>

//               <div className={styles.settingRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Data Analytics</div>
//                   <div className={styles.settingHelp}>Allow anonymous usage data to improve SafeGuard</div>
//                 </div>
//                 <label className={styles.toggleSwitch}>
//                   <input type="checkbox" defaultChecked />
//                   <span className={styles.slider}></span>
//                 </label>
//               </div>
//             </section>

//             {/* Security & Authentication */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Security & Authentication</h2>

//               <div className={styles.securityRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Two-Factor Authentication</div>
//                   <div className={styles.settingHelp}>Enabled via authenticator app (not configured)</div>
//                 </div>
//                 <span className={styles.badgeSuccess}>Enabled</span> {/* Placeholder */}
//               </div>

//               <div className={styles.securityRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Password</div>
//                   <div className={styles.settingHelp}>Last changed recently</div>
//                 </div>
//                 <button className={styles.btnOutlineSmall} onClick={handleChangePassword}>
//                   Change Password
//                 </button>
//               </div>

//               <div className={styles.securityRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Active Sessions</div>
//                   <div className={styles.settingHelp}>Current device</div>
//                 </div>
//                 <button className={styles.btnOutlineSmall}>Manage Sessions</button>
//               </div>
//             </section>

//             {/* Emergency Contacts */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Emergency Contacts</h2>

//               <div className={styles.emergencyCard}>
//                 <div className={styles.emergencyIcon}>
//                   <Icon icon="lucide:phone-call" />
//                 </div>
//                 <div>
//                   <h3>Primary Emergency Contact</h3>
//                   <p>Not set yet</p> {/* Placeholder - add form later */}
//                 </div>
//                 <button className={styles.editBtn}>Edit</button>
//               </div>

//               <button className={styles.addBtn}>
//                 <Icon icon="lucide:plus" />
//                 Add Emergency Contact
//               </button>
//             </section>

//             {/* Data Export & Delete */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Account Data & Export</h2>

//               <div className={styles.dataActions}>
//                 <div className={styles.dataCard}>
//                   <Icon icon="lucide:download" className={styles.dataIcon} />
//                   <div>
//                     <h3>Export Data</h3>
//                     <p>Download all your account data</p>
//                   </div>
//                   <button className={styles.btnPrimarySmall}>Request Export</button>
//                 </div>

//                 <div className={styles.dataCard}>
//                   <Icon icon="lucide:activity" className={styles.dataIcon} />
//                   <div>
//                     <h3>Activity Log</h3>
//                     <p>View recent account activity</p>
//                   </div>
//                   <button className={styles.btnOutlineSmall}>View Log</button>
//                 </div>
//               </div>

//               <div className={styles.dangerZone}>
//                 <h3>Delete Account</h3>
//                 <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
//                 <button className={styles.btnDanger} onClick={handleDeleteAccount}>
//                   Delete My Account
//                 </button>
//               </div>
//             </section>
//           </div>
//         </div>
//       </div>
//     </main>
//   );
// }
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './AccountSettings.module.css';

// // Dummy user data – in real app from auth/state
// const dummyUser = {
//   name: "Sarah Chen",
//   avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300",
//   role: "Human Rights Advocate • SecureNet Foundation",
//   email: "sarah.chen@securenet.org",
//   phone: "+1 (415) 555-0123",
//   location: "San Francisco, CA",
//   bio: "Dedicated human rights advocate with 12+ years experience in digital security and activist protection. Passionate about building resilient communities through knowledge sharing.",
//   verified: true,
//   twoFactor: true,
//   lastPasswordChange: "3 months ago",
//   activeSession: "Chrome on macOS • San Francisco, CA (Current)",
// };

// export default function AccountSettings() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Back / Title */}
//         <header className={styles.pageHeader}>
//           <Link to="/" className={styles.backLink}>
//             <Icon icon="lucide:arrow-left" />
//             Back to Home
//           </Link>
//           <h1 className={styles.pageTitle}>Account Settings</h1>
//         </header>

//         <div className={styles.layout}>
//           {/* Sidebar Navigation */}
//           <aside className={styles.sidebar}>
//             <nav className={styles.navList}>
//               <Link to="/account-settings" className={`${styles.navItem} ${styles.active}`}>
//                 <Icon icon="lucide:user" />
//                 Personal Information
//               </Link>
//               <Link to="/account-settings/organization" className={styles.navItem}>
//                 <Icon icon="lucide:building" />
//                 Organization & Role
//               </Link>
//               <Link to="/account-settings/privacy" className={styles.navItem}>
//                 <Icon icon="lucide:shield" />
//                 Privacy Controls
//               </Link>
//               <Link to="/account-settings/security" className={styles.navItem}>
//                 <Icon icon="lucide:lock" />
//                 Password & Authentication
//               </Link>
//               <Link to="/account-settings/notifications" className={styles.navItem}>
//                 <Icon icon="lucide:bell" />
//                 Notification Preferences
//               </Link>
//               <Link to="/account-settings/emergency" className={styles.navItem}>
//                 <Icon icon="lucide:alert-triangle" />
//                 Emergency Alerts & Contacts
//               </Link>
//               <Link to="/account-settings/data" className={styles.navItem}>
//                 <Icon icon="lucide:download" />
//                 Data Export & Activity
//               </Link>
//               <Link to="/support" className={styles.navItem}>
//                 <Icon icon="lucide:help-circle" />
//                 Help & Support
//               </Link>
//               <button className={`${styles.navItem} ${styles.danger}`}>
//                 <Icon icon="lucide:trash-2" />
//                 Delete Account
//               </button>
//             </nav>
//           </aside>

//           {/* Main Content */}
//           <div className={styles.content}>
//             {/* Profile Section */}
//             <section className={styles.section}>
//               <div className={styles.profileHeader}>
//                 <div className={styles.avatarWrapper}>
//                   <img src={dummyUser.avatar} alt={dummyUser.name} className={styles.avatar} />
//                   <button className={styles.editAvatar}>
//                     <Icon icon="lucide:camera" />
//                   </button>
//                 </div>

//                 <div className={styles.profileInfo}>
//                   <div className={styles.nameRow}>
//                     <h2 className={styles.name}>{dummyUser.name}</h2>
//                     {dummyUser.verified && (
//                       <span className={styles.verifiedBadge}>
//                         <Icon icon="lucide:shield-check" />
//                         Verified
//                       </span>
//                     )}
//                   </div>
//                   <p className={styles.role}>{dummyUser.role}</p>

//                   <div className={styles.profileActions}>
//                     <button className={styles.btnOutline}>Edit Profile</button>
//                     <Link to="/profile/public" className={styles.btnOutline}>
//                       View Public Profile
//                     </Link>
//                   </div>
//                 </div>
//               </div>

//               <form className={styles.formGrid}>
//                 <div className={styles.formField}>
//                   <label>Full Name</label>
//                   <input type="text" defaultValue={dummyUser.name} />
//                 </div>
//                 <div className={styles.formField}>
//                   <label>Email Address</label>
//                   <input type="email" defaultValue={dummyUser.email} />
//                 </div>
//                 <div className={styles.formField}>
//                   <label>Phone Number</label>
//                   <input type="tel" defaultValue={dummyUser.phone} />
//                 </div>
//                 <div className={styles.formField}>
//                   <label>Location</label>
//                   <input type="text" defaultValue={dummyUser.location} />
//                 </div>
//                 <div className={styles.formFieldFull}>
//                   <label>Bio</label>
//                   <textarea defaultValue={dummyUser.bio} rows={4} />
//                 </div>
//               </form>

//               <div className={styles.formActions}>
//                 <button className={styles.btnPrimary}>Save Changes</button>
//               </div>
//             </section>

//             {/* Privacy & Sharing */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Privacy & Sharing</h2>

//               <div className={styles.settingRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Resource Visibility</div>
//                   <div className={styles.settingHelp}>Who can see your saved/bookmarked resources</div>
//                 </div>
//                 <select className={styles.select}>
//                   <option>Verified Members Only</option>
//                   <option>Organization Members</option>
//                   <option>Public</option>
//                   <option>Private</option>
//                 </select>
//               </div>

//               <div className={styles.settingRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Activity Status</div>
//                   <div className={styles.settingHelp}>Show when you were last active</div>
//                 </div>
//                 <label className={styles.toggleSwitch}>
//                   <input type="checkbox" defaultChecked />
//                   <span className={styles.slider}></span>
//                 </label>
//               </div>

//               <div className={styles.settingRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Data Analytics</div>
//                   <div className={styles.settingHelp}>Allow anonymous usage data to improve SafeGuard</div>
//                 </div>
//                 <label className={styles.toggleSwitch}>
//                   <input type="checkbox" defaultChecked />
//                   <span className={styles.slider}></span>
//                 </label>
//               </div>
//             </section>

//             {/* Security & Authentication */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Security & Authentication</h2>

//               <div className={styles.securityRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Two-Factor Authentication</div>
//                   <div className={styles.settingHelp}>Enabled via authenticator app</div>
//                 </div>
//                 <span className={styles.badgeSuccess}>Enabled</span>
//               </div>

//               <div className={styles.securityRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Password</div>
//                   <div className={styles.settingHelp}>Last changed {dummyUser.lastPasswordChange}</div>
//                 </div>
//                 <button className={styles.btnOutlineSmall}>Change Password</button>
//               </div>

//               <div className={styles.securityRow}>
//                 <div>
//                   <div className={styles.settingLabel}>Active Sessions</div>
//                   <div className={styles.settingHelp}>{dummyUser.activeSession}</div>
//                 </div>
//                 <button className={styles.btnOutlineSmall}>Manage Sessions</button>
//               </div>
//             </section>

//             {/* Emergency Contacts */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Emergency Contacts</h2>

//               <div className={styles.emergencyCard}>
//                 <div className={styles.emergencyIcon}>
//                   <Icon icon="lucide:phone-call" />
//                 </div>
//                 <div>
//                   <h3>Primary Emergency Contact</h3>
//                   <p>Maria Rodriguez (Legal Counsel) • +1 (555) 123-4567 • 24/7</p>
//                 </div>
//                 <button className={styles.editBtn}>Edit</button>
//               </div>

//               <button className={styles.addBtn}>
//                 <Icon icon="lucide:plus" />
//                 Add Emergency Contact
//               </button>
//             </section>

//             {/* Data Export & Delete */}
//             <section className={styles.section}>
//               <h2 className={styles.sectionTitle}>Account Data & Export</h2>

//               <div className={styles.dataActions}>
//                 <div className={styles.dataCard}>
//                   <Icon icon="lucide:download" className={styles.dataIcon} />
//                   <div>
//                     <h3>Export Data</h3>
//                     <p>Download all your account data</p>
//                   </div>
//                   <button className={styles.btnPrimarySmall}>Request Export</button>
//                 </div>

//                 <div className={styles.dataCard}>
//                   <Icon icon="lucide:activity" className={styles.dataIcon} />
//                   <div>
//                     <h3>Activity Log</h3>
//                     <p>View recent account activity</p>
//                   </div>
//                   <button className={styles.btnOutlineSmall}>View Log</button>
//                 </div>
//               </div>

//               <div className={styles.dangerZone}>
//                 <h3>Delete Account</h3>
//                 <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
//                 <button className={styles.btnDanger}>Delete My Account</button>
//               </div>
//             </section>
//           </div>
//         </div>
//       </div>
//     </main>
//   );
// }
// import { Icon } from '@iconify/react';
// import styles from './AccountSettings.module.css';

// export default function AccountSettings() {
//   return (
//     <main className={styles.main}>
//       {/* Sidebar Navigation */}
//       <aside className={styles.sidebar}>
//         <h2 className={styles.sidebarTitle}>Account Settings</h2>

//         <div className={styles.sidebarSection}>
//           <div className={styles.sectionHeader}>Profile</div>
//           <a href="#" className={`${styles.sidebarItem} ${styles.active}`}>
//             <Icon icon="lucide:user" className={styles.icon} />
//             Personal Information
//           </a>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:building" className={styles.icon} />
//             Organization & Role
//           </a>
//         </div>

//         <div className={styles.sidebarSection}>
//           <div className={styles.sectionHeader}>Privacy & Security</div>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:shield" className={styles.icon} />
//             Privacy Controls
//           </a>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:lock" className={styles.icon} />
//             Password & Authentication
//           </a>
//         </div>

//         <div className={styles.sidebarSection}>
//           <div className={styles.sectionHeader}>Notifications</div>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:bell" className={styles.icon} />
//             Notification Preferences
//           </a>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:alert-triangle" className={styles.icon} />
//             Emergency Alerts
//           </a>
//         </div>

//         <div className={styles.sidebarSection}>
//           <div className={styles.sectionHeader}>Resources</div>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:share-2" className={styles.icon} />
//             Resource Sharing
//           </a>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:phone-call" className={styles.icon} />
//             Emergency Contacts
//           </a>
//         </div>

//         <div className={styles.sidebarSection}>
//           <div className={styles.sectionHeader}>Account & Data</div>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:download" className={styles.icon} />
//             Data Export
//           </a>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:activity" className={styles.icon} />
//             Account Activity
//           </a>
//         </div>

//         <div className={styles.sidebarSection}>
//           <div className={styles.sectionHeader}>Support</div>
//           <a href="#" className={styles.sidebarItem}>
//             <Icon icon="lucide:help-circle" className={styles.icon} />
//             Help & Support
//           </a>
//           <a href="#" className={`${styles.sidebarItem} ${styles.danger}`}>
//             <Icon icon="lucide:trash-2" className={styles.icon} />
//             Delete Account
//           </a>
//         </div>
//       </aside>

//       {/* Main Content Area */}
//       <div className={styles.contentArea}>
//         <section className={styles.section}>
//           <div className={styles.profileHeader}>
//             <div className={styles.avatarWrapper}>
//               <img
//                 src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300"
//                 alt="Profile"
//                 className={styles.avatar}
//               />
//               <button className={styles.editAvatarBtn}>
//                 <Icon icon="lucide:camera" />
//               </button>
//             </div>

//             <div className={styles.profileInfo}>
//               <div className={styles.nameRow}>
//                 <h1 className={styles.name}>Sarah Chen</h1>
//                 <span className={styles.verifiedBadge}>
//                   <Icon icon="lucide:shield-check" />
//                   Verified
//                 </span>
//               </div>
//               <p className={styles.role}>Human Rights Advocate • SecureNet Foundation</p>
//               <div className={styles.profileActions}>
//                 <button className={styles.btnOutline}>Edit Profile</button>
//                 <button className={styles.btnOutline}>View Public Profile</button>
//               </div>
//             </div>
//           </div>

//           <form className={styles.formGrid}>
//             <div className={styles.formField}>
//               <label>Full Name</label>
//               <input type="text" defaultValue="Sarah Chen" />
//             </div>

//             <div className={styles.formField}>
//               <label>Email Address</label>
//               <input type="email" defaultValue="sarah.chen@securenet.org" />
//             </div>

//             <div className={styles.formField}>
//               <label>Phone Number</label>
//               <input type="tel" defaultValue="+1 (415) 555-0123" />
//             </div>

//             <div className={styles.formField}>
//               <label>Location</label>
//               <input type="text" defaultValue="San Francisco, CA" />
//             </div>

//             <div className={styles.formFieldFull}>
//               <label>Bio</label>
//               <textarea
//                 defaultValue="Dedicated human rights advocate with 12+ years experience in digital security and activist protection..."
//               />
//             </div>
//           </form>

//           <div className={styles.formActions}>
//             <button className={styles.btnPrimary}>Save Changes</button>
//           </div>
//         </section>

//         {/* Privacy & Sharing Section */}
//         <section className={styles.section}>
//           <h2 className={styles.sectionTitle}>Privacy & Sharing</h2>

//           <div className={styles.settingRow}>
//             <div>
//               <div className={styles.settingLabel}>Resource Visibility</div>
//               <div className={styles.settingHelp}>Control who can see your saved resources</div>
//             </div>
//             <select className={styles.select}>
//               <option>Verified Members Only</option>
//               <option>Organization Members</option>
//               <option>Public</option>
//               <option>Private</option>
//             </select>
//           </div>

//           <div className={styles.settingRow}>
//             <div>
//               <div className={styles.settingLabel}>Show Activity Status</div>
//               <div className={styles.settingHelp}>Display when you were last active</div>
//             </div>
//             <label className={styles.toggle}>
//               <input type="checkbox" defaultChecked />
//               <span className={styles.toggleSlider}></span>
//             </label>
//           </div>

//           {/* more toggles / settings ... */}
//         </section>

//         {/* Security Section */}
//         <section className={styles.section}>
//           <h2 className={styles.sectionTitle}>Security & Authentication</h2>

//           <div className={styles.securityRow}>
//             <div>
//               <div className={styles.settingLabel}>Two-Factor Authentication</div>
//               <div className={styles.settingHelp}>Enabled via authenticator app</div>
//             </div>
//             <div className={styles.badgeSuccess}>Enabled</div>
//           </div>

//           <div className={styles.securityRow}>
//             <div>
//               <div className={styles.settingLabel}>Active Sessions</div>
//               <div className={styles.settingHelp}>Chrome on macOS • San Francisco, CA (Current)</div>
//             </div>
//             <button className={styles.btnOutlineSmall}>Manage Sessions</button>
//           </div>
//         </section>

//         {/* Emergency Contacts */}
//         <section className={styles.section}>
//           <h2 className={styles.sectionTitle}>Emergency Contacts</h2>
//           <div className={styles.alertBoxDanger}>
//             <Icon icon="lucide:phone-call" />
//             <div>
//               <strong>Primary Emergency Contact</strong>
//               <p>Maria Rodriguez (Legal Counsel) • +1 (555) 123-4567</p>
//             </div>
//           </div>
//           <button className={styles.btnOutline}>
//             <Icon icon="lucide:plus" /> Add Contact
//           </button>
//         </section>

//         {/* Data Export & Delete */}
//         <section className={styles.section}>
//           <h2 className={styles.sectionTitle}>Account Data & Export</h2>

//           <div className={styles.dataActions}>
//             <div className={styles.dataCard}>
//               <Icon icon="lucide:download" className={styles.dataIcon} />
//               <div>
//                 <h3>Export Data</h3>
//                 <p>Download your account data</p>
//               </div>
//               <button className={styles.btnPrimary}>Request Export</button>
//             </div>

//             <div className={styles.dataCard}>
//               <Icon icon="lucide:activity" className={styles.dataIcon} />
//               <div>
//                 <h3>Activity Log</h3>
//                 <p>View account activity</p>
//               </div>
//               <button className={styles.btnOutline}>View Log</button>
//             </div>
//           </div>

//           <div className={styles.dangerZone}>
//             <h3>Delete Account</h3>
//             <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
//             <button className={styles.btnDanger}>Delete My Account</button>
//           </div>
//         </section>
//       </div>
//     </main>
//   );
// }